import React, {useEffect, useState} from "react";
import Form from "components/molecules/Form/Form";
import Field from "components/molecules/Field";
import {useForm} from "components/molecules/Form";
import Button from "components/molecules/Button";
import {FaPlus} from "react-icons/fa";
import {MdDelete} from "react-icons/md";
import {NewIngredientsWrap} from "./AddIngredients.styles";

const AddIngredients = ({onClose, setIngredients, ingredientsData, isEdit}) => {
    const [form] = useForm();

    // New state structure to handle grouped ingredients
    const [formState, setFormState] = useState([{
        id: Date.now(), // Add unique ID for each group
        groupName: "",
        ingredients: [{id: Date.now() + 1, ingredientName: ""}]
    }]);
    const [nextId, setNextId] = useState(Date.now() + 100);

    useEffect(() => {
        if (isEdit && ingredientsData && ingredientsData.length > 0) {
            console.log("AddIngredients - received ingredientsData (new structure):", ingredientsData);
            
            // Handle new nested structure
            const updatedState = ingredientsData.map((group, index) => ({
                id: Date.now() + index,
                groupName: group.groupName || "",
                ingredients: (group.ingredients || [{ingredientName: ""}]).map((ingredient, iIndex) => ({
                    id: Date.now() + index * 1000 + iIndex,
                    ingredientName: ingredient.ingredientName || ""
                }))
            }));
            setNextId(Date.now() + ingredientsData.length * 1000 + 1000);
            
            console.log("AddIngredients - updatedState (new structure):", updatedState);
            setFormState(updatedState);

            // Set form field values for the new structure
            const fieldValues = {};
            updatedState.forEach((group, groupIndex) => {
                fieldValues[`groupName${groupIndex}`] = group.groupName;
                group.ingredients.forEach((ingredient, ingredientIndex) => {
                    fieldValues[`ingredientName${groupIndex}_${ingredientIndex}`] = ingredient.ingredientName;
                });
            });

            console.log("AddIngredients - fieldValues (new structure):", fieldValues);
            form.setFieldsValue(fieldValues);
        }
    }, [isEdit, ingredientsData, form]);

    const handleSubmit = () => {
        // Clean the data by removing IDs before sending
        const cleanedData = formState.map(group => ({
            groupName: group.groupName,
            ingredients: group.ingredients.map(ingredient => ({
                ingredientName: ingredient.ingredientName
            }))
        }));
        setIngredients(cleanedData);
        onClose();
    };

    const addGroup = () => {
        const newId = nextId;
        setNextId(prev => prev + 1);
        const newFormState = [...formState, {
            id: newId,
            groupName: "",
            ingredients: [{id: newId + 1, ingredientName: ""}]
        }];
        
        // Set field values BEFORE updating state to prevent validation errors
        const fieldValues = {};
        newFormState.forEach((group, gIndex) => {
            fieldValues[`groupName${gIndex}`] = group.groupName;
            group.ingredients.forEach((ingredient, iIndex) => {
                fieldValues[`ingredientName${gIndex}_${iIndex}`] = ingredient.ingredientName;
            });
        });
        form.setFieldsValue(fieldValues);
        
        setFormState(newFormState);
        setNextId(prev => prev + 1);
    };

    const addIngredientToGroup = (groupIndex) => {
        const newId = nextId;
        setNextId(prev => prev + 1);
        const newFormState = formState.map((group, i) => 
            i === groupIndex 
                ? {...group, ingredients: [...group.ingredients, {id: newId, ingredientName: ""}]}
                : group
        );
        
        // Set field values BEFORE updating state to prevent validation errors
        const fieldValues = {};
        newFormState.forEach((group, gIndex) => {
            fieldValues[`groupName${gIndex}`] = group.groupName;
            group.ingredients.forEach((ingredient, iIndex) => {
                fieldValues[`ingredientName${gIndex}_${iIndex}`] = ingredient.ingredientName;
            });
        });
        form.setFieldsValue(fieldValues);
        
        setFormState(newFormState);
    };

    const handleGroupNameChange = (groupIndex, value) => {
        setFormState(prevState => prevState.map((group, i) => 
            i === groupIndex ? {...group, groupName: value} : group
        ));
        form.setFieldsValue({[`groupName${groupIndex}`]: value});
    };

    const handleIngredientChange = (groupIndex, ingredientIndex, field, value) => {
        setFormState(prevState => prevState.map((group, i) => 
            i === groupIndex 
                ? {
                    ...group, 
                    ingredients: group.ingredients.map((ingredient, j) => 
                        j === ingredientIndex ? {...ingredient, [field]: value} : ingredient
                    )
                }
                : group
        ));
        form.setFieldsValue({[`${field}${groupIndex}_${ingredientIndex}`]: value});
    };

    const removeGroup = (groupIndex) => {
        const newFormState = formState.filter((_, i) => i !== groupIndex);
        setFormState(newFormState);
        
        // Clear all form fields and reset with new indices
        form.resetFields();
        
        // Re-populate form with correct field names after removal
        setTimeout(() => {
            const fieldValues = {};
            newFormState.forEach((group, newGroupIndex) => {
                fieldValues[`groupName${newGroupIndex}`] = group.groupName;
                group.ingredients.forEach((ingredient, ingredientIndex) => {
                    fieldValues[`ingredientName${newGroupIndex}_${ingredientIndex}`] = ingredient.ingredientName;
                });
            });
            form.setFieldsValue(fieldValues);
        }, 0);
    };

    const removeIngredient = (groupIndex, ingredientIndex) => {
        const newFormState = formState.map((group, i) => 
            i === groupIndex 
                ? {...group, ingredients: group.ingredients.filter((_, j) => j !== ingredientIndex)}
                : group
        );
        setFormState(newFormState);
        
        // Clear and reset form fields for this group's ingredients
        setTimeout(() => {
            const fieldValues = {};
            newFormState.forEach((group, gIndex) => {
                fieldValues[`groupName${gIndex}`] = group.groupName;
                group.ingredients.forEach((ingredient, iIndex) => {
                    fieldValues[`ingredientName${gIndex}_${iIndex}`] = ingredient.ingredientName;
                });
            });
            form.setFieldsValue(fieldValues);
        }, 0);
    };

    return (
        <>
            <NewIngredientsWrap>
                <Form form={form} onSubmit={() => handleSubmit()} className="form-wrap">
                    {formState.map((group, groupIndex) => (
                        <div key={group.id} style={{marginBottom: "30px", border: "1px solid #e0e0e0", padding: "20px", borderRadius: "8px"}}>
                            <div className="delete-btn" style={{marginBottom: "15px"}}>
                                <Button danger sm type="button" onClick={() => removeGroup(groupIndex)}>
                                    <MdDelete />
                                    Delete Group
                                </Button>
                            </div>
                            
                            {/* Group Name */}
                            <Form.Item
                                label="Group Name"
                                type="text"
                                name={`groupName${groupIndex}`}
                                lg
                                placeholder="e.g., Toppings, Sauces, Extras"
                                $bgWhite
                                value={group.groupName}
                                onChange={e => handleGroupNameChange(groupIndex, e.target.value)}
                                rules={[{required: true, message: "Group Name is required"}]}>
                                <Field />
                            </Form.Item>

                            {/* Ingredients in this group */}
                            <div style={{marginLeft: "20px"}}>
                                <h4 style={{marginBottom: "15px"}}>Ingredients in {group.groupName || "this group"}:</h4>
                                {group.ingredients.map((ingredient, ingredientIndex) => (
                                    <div key={ingredient.id} style={{marginBottom: "15px", padding: "15px", backgroundColor: "#f9f9f9", borderRadius: "5px"}}>
                                        <div className="delete-btn" style={{marginBottom: "10px"}}>
                                            <Button danger sm type="button" onClick={() => removeIngredient(groupIndex, ingredientIndex)}>
                                                <MdDelete />
                                                Remove Ingredient
                                            </Button>
                                        </div>
                                        
                                        <div style={{display: "flex", gap: "15px"}}>
                                            <Form.Item
                                                label="Ingredient Name"
                                                type="text"
                                                name={`ingredientName${groupIndex}_${ingredientIndex}`}
                                                lg
                                                placeholder="e.g., Lettuce"
                                                $bgWhite
                                                value={ingredient.ingredientName}
                                                onChange={e => handleIngredientChange(groupIndex, ingredientIndex, "ingredientName", e.target.value)}
                                                rules={[{required: true, message: "Ingredient name is required"}]}>
                                                <Field />
                                            </Form.Item>
                                            

                                        </div>
                                    </div>
                                ))}
                                
                                <Button sm gray type="button" onClick={() => addIngredientToGroup(groupIndex)}>
                                    <FaPlus />
                                    Add Ingredient to Group
                                </Button>
                            </div>
                        </div>
                    ))}

                    <div className="btn-holder">
                        <div className="add-btn">
                            <Button lg gray type="button" onClick={addGroup}>
                                <FaPlus />
                                Add New Group
                            </Button>
                        </div>
                        <div className="save-btn">
                            <Button lg type="submit">
                                Save
                            </Button>
                        </div>
                    </div>
                </Form>
            </NewIngredientsWrap>
        </>
    );
};

export default AddIngredients;
